import { useState, useEffect, useRef, forwardRef } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface PopupVideo {
  id: string;
  title: string;
  file_url: string;
  is_active: boolean;
}

const VideoOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
VideoOverlay.displayName = "VideoOverlay";

const VideoContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <VideoOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
VideoContent.displayName = "VideoContent";

const VideoPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState<PopupVideo | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const fetchActiveVideo = async () => {
      try {
        const { data, error } = await supabase
          .from('popup_videos')
          .select('*')
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        if (error || !data) {
          // Silently handle - no active video is a valid state
          return;
        }

        setActiveVideo(data);

        // Show popup every time user visits the site
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 500);
        
        return () => clearTimeout(timer);
      } catch {
        // Silently handle network errors
      }
    };

    fetchActiveVideo();
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleVideoEnd = () => {
    // Auto-close after video ends
    handleClose();
  };

  if (!activeVideo) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <VideoContent className="w-[90vw] max-w-[90vw] p-0 bg-black border-none rounded-lg overflow-hidden">
        {/* Close button - top right, next to volume icon */}
        <button
          onClick={handleClose}
          className="absolute right-14 top-3 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
          aria-label="Close video"
        >
          <X className="h-5 w-5" />
        </button>
        <video
          ref={videoRef}
          src={activeVideo.file_url}
          autoPlay
          playsInline
          controls
          onEnded={handleVideoEnd}
          className="w-full h-auto max-h-[90vh] object-contain"
        />
      </VideoContent>
    </DialogPrimitive.Root>
  );
};

export default VideoPopup;
